/**
 * App Component - Root Application Component
 *
 * Auth.HireNow.com - Central Authentication SPA
 *
 * This is the root component that sets up:
 * - React Router for navigation
 * - Authentication Provider for auth state
 * - Route definitions for the application
 *
 * Flow:
 * 1. User lands on / or /login (LoginPage)
 * 2. User clicks "Sign in with Microsoft"
 * 3. Firebase handles authentication via popup
 * 4. Backend sets custom claims (role, organisationId)
 * 5. On success, user is redirected to /redirect
 * 6. AuthRedirectHandler reads roles and redirects to downstream app
 *    (or uses returnTo if provided)
 *
 * Migration Note (2026-02-22):
 * Updated for Firebase Authentication per Architecture Guardrail 1-006.
 */

import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth';
import { LoginPage, Loading } from './components';
import { AuthRedirectHandler, LogoutHandler } from './routing';
import { RoleNavigationPage } from './views';

/**
 * Stores returnTo URL in sessionStorage for use after authentication.
 * Called from /login route to capture the redirect destination.
 */
function storeReturnTo(returnTo: string | null): void {
  if (returnTo) {
    sessionStorage.setItem('auth_returnTo', returnTo);
  }
}

/**
 * LoginRoute Component
 * 
 * Handles /login route which may include a returnTo query parameter.
 * Stores returnTo and redirects to the main login flow.
 */
function LoginRoute() {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading } = useAuth();
  
  // Store returnTo if provided
  const returnTo = searchParams.get('returnTo');
  if (returnTo) {
    storeReturnTo(returnTo);
  }
  
  if (isLoading) {
    return <Loading fullScreen message="Initializing..." />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/redirect" replace />;
  }
  
  return <LoginPage />;
}

/**
 * AppRoutes Component
 *
 * Defines the application routes with authentication-aware navigation.
 * Uses the auth context to determine which routes to show.
 */
function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading screen during initial auth check
  if (isLoading) {
    return <Loading fullScreen message="Initializing..." />;
  }

  return (
    <Routes>
      {/* Login Page - shown when not authenticated */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            // Already authenticated, redirect to handler
            <Navigate to="/redirect" replace />
          ) : (
            <LoginPage />
          )
        }
      />

      {/* Login route with returnTo support */}
      <Route path="/login" element={<LoginRoute />} />

      {/* Auth Redirect Handler - processes post-login redirection */}
      <Route
        path="/redirect"
        element={
          isAuthenticated ? (
            <AuthRedirectHandler />
          ) : (
            // Not authenticated, go back to login
            <Navigate to="/" replace />
          )
        }
      />

      {/* Organisation/Role Selection Page */}
      <Route
        path="/select"
        element={
          isAuthenticated ? (
            <RoleNavigationPage />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      {/* Callback route for Firebase redirect flow (if used) */}
      <Route
        path="/auth/callback"
        element={
          // Firebase will handle the callback automatically
          // This route exists for compatibility
          <Loading fullScreen message="Completing sign-in..." />
        }
      />

      {/* Logout handler - signs out and redirects */}
      <Route path="/logout" element={<LogoutHandler />} />

      {/* 404 - Catch all unmatched routes */}
      <Route
        path="*"
        element={
          <div className="not-found-page">
            <h1>Page Not Found</h1>
            <p>The page you're looking for doesn't exist.</p>
            <a href="/" className="btn btn-primary">
              Go to Login
            </a>
          </div>
        }
      />
    </Routes>
  );
}

/**
 * App Component
 *
 * The root component that wraps the application with necessary providers.
 */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app">
          <AppRoutes />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
